/**
 * Turn-Based Chess Service
 * 
 * Handles turn-based chess game functionality including:
 * - Game state persistence after each move
 * - Loading games when player returns
 * - Supporting multiple active games per player
 * - Turn notifications
 */

import { GameState, PlayerColor, Position, PieceType } from '../types';
import { LocalStorageManager } from '../../services/LocalStorageManager';
import { NotificationService } from '../../services/NotificationService';

export interface TurnBasedGame {
  gameId: string;
  players: {
    white: {
      id: string;
      name: string;
    };
    black: {
      id: string;
      name: string;
    };
  };
  gameState: GameState;
  currentTurn: PlayerColor;
  lastMoveAt: number;
  createdAt: number;
  expiresAt: number; // 7 days from last move
}

export interface TurnBasedMove {
  gameId: string;
  from: Position;
  to: Position;
  promotion?: PieceType;
  timestamp: number;
}

export class TurnBasedChessService {
  private storage: LocalStorageManager;
  private notifications: NotificationService;
  private readonly STORAGE_KEY = 'chess_turn_based_games';
  private readonly GAME_EXPIRY_DAYS = 7;

  constructor(storage: LocalStorageManager, notifications: NotificationService) {
    this.storage = storage;
    this.notifications = notifications;
  }

  /**
   * Save games to storage (using localStorage directly for turn-based games)
   */
  private saveAllGames(games: TurnBasedGame[]): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(games));
    } catch (error) {
      console.error('Failed to save games to storage:', error);
    }
  }

  /**
   * Load games from storage (using localStorage directly for turn-based games)
   */
  private loadAllGames(): TurnBasedGame[] {
    try {
      const data = localStorage.getItem(this.STORAGE_KEY);
      return data ? JSON.parse(data) : [];
    } catch (error) {
      console.error('Failed to load games from storage:', error);
      return [];
    }
  }

  /**
   * Create a new turn-based game
   */
  createGame(
    whitePlayerId: string,
    whitePlayerName: string,
    blackPlayerId: string,
    blackPlayerName: string,
    initialGameState: GameState
  ): TurnBasedGame {
    const gameId = this.generateGameId();
    const now = Date.now();
    const expiresAt = now + this.GAME_EXPIRY_DAYS * 24 * 60 * 60 * 1000;

    const game: TurnBasedGame = {
      gameId,
      players: {
        white: { id: whitePlayerId, name: whitePlayerName },
        black: { id: blackPlayerId, name: blackPlayerName }
      },
      gameState: initialGameState,
      currentTurn: 'white',
      lastMoveAt: now,
      createdAt: now,
      expiresAt
    };

    this.saveGame(game);
    return game;
  }

  /**
   * Save game state after a move
   */
  saveGameState(gameId: string, gameState: GameState): boolean {
    try {
      const game = this.loadGame(gameId);
      if (!game) {
        console.error('Game not found:', gameId);
        return false;
      }

      game.gameState = gameState;
      game.currentTurn = gameState.currentPlayer;
      game.lastMoveAt = Date.now();
      game.expiresAt = Date.now() + this.GAME_EXPIRY_DAYS * 24 * 60 * 60 * 1000;

      this.saveGame(game);

      // Notify opponent it's their turn
      this.notifyOpponentTurn(game);

      return true;
    } catch (error) {
      console.error('Failed to save game state:', error);
      return false;
    }
  }

  /**
   * Load a specific game
   */
  loadGame(gameId: string): TurnBasedGame | null {
    try {
      const games = this.loadAllGames();
      return games.find(g => g.gameId === gameId) || null;
    } catch (error) {
      console.error('Failed to load game:', error);
      return null;
    }
  }

  /**
   * Load all active games for a player
   */
  loadPlayerGames(playerId: string): TurnBasedGame[] {
    try {
      const allGames = this.loadAllGames();
      const now = Date.now();

      // Filter games where player is involved and not expired
      return allGames.filter(game => {
        const isPlayer = game.players.white.id === playerId || game.players.black.id === playerId;
        const notExpired = game.expiresAt > now;
        const notFinished = game.gameState.gameStatus.type === 'active' || 
                           game.gameState.gameStatus.type === 'check';
        return isPlayer && notExpired && notFinished;
      });
    } catch (error) {
      console.error('Failed to load player games:', error);
      return [];
    }
  }

  /**
   * Load games where it's the player's turn
   */
  loadPlayerTurnGames(playerId: string): TurnBasedGame[] {
    try {
      const playerGames = this.loadPlayerGames(playerId);
      return playerGames.filter(game => {
        const playerColor = game.players.white.id === playerId ? 'white' : 'black';
        return game.currentTurn === playerColor;
      });
    } catch (error) {
      console.error('Failed to load player turn games:', error);
      return [];
    }
  }

  /**
   * Delete a game
   */
  deleteGame(gameId: string): boolean {
    try {
      const games = this.loadAllGames();
      const filteredGames = games.filter(g => g.gameId !== gameId);
      this.saveAllGames(filteredGames);
      return true;
    } catch (error) {
      console.error('Failed to delete game:', error);
      return false;
    }
  }

  /**
   * Clean up expired games
   */
  cleanupExpiredGames(): number {
    try {
      const games = this.loadAllGames();
      const now = Date.now();
      const activeGames = games.filter(game => game.expiresAt > now);
      const expiredCount = games.length - activeGames.length;

      if (expiredCount > 0) {
        this.saveAllGames(activeGames);
      }

      return expiredCount;
    } catch (error) {
      console.error('Failed to cleanup expired games:', error);
      return 0;
    }
  }

  /**
   * Get game count for a player
   */
  getPlayerGameCount(playerId: string): number {
    return this.loadPlayerGames(playerId).length;
  }

  /**
   * Check if player can start a new game (limit to prevent abuse)
   */
  canStartNewGame(playerId: string, maxGames: number = 10): boolean {
    return this.getPlayerGameCount(playerId) < maxGames;
  }

  /**
   * Generate a unique game ID
   */
  private generateGameId(): string {
    return `chess_tb_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Save a game to storage
   */
  private saveGame(game: TurnBasedGame): void {
    const games = this.loadAllGames();
    const existingIndex = games.findIndex(g => g.gameId === game.gameId);

    if (existingIndex >= 0) {
      games[existingIndex] = game;
    } else {
      games.push(game);
    }

    this.saveAllGames(games);
  }

  /**
   * Notify opponent that it's their turn
   */
  private notifyOpponentTurn(game: TurnBasedGame): void {
    try {
      const opponentColor = game.currentTurn;
      const opponentName = game.players[opponentColor].name;

      // TODO: Fix notification type compatibility
      // this.notifications.addNotification({
      //   id: `turn_${game.gameId}`,
      //   type: 'info',
      //   message: `It's ${opponentName}'s turn in your chess game`,
      //   duration: 5000
      // });
      console.log(`It's ${opponentName}'s turn in chess game ${game.gameId}`);
    } catch (error) {
      console.error('Failed to send turn notification:', error);
    }
  }

  /**
   * Get opponent info for a game
   */
  getOpponent(game: TurnBasedGame, playerId: string): { id: string; name: string; color: PlayerColor } | null {
    if (game.players.white.id === playerId) {
      return { ...game.players.black, color: 'black' };
    } else if (game.players.black.id === playerId) {
      return { ...game.players.white, color: 'white' };
    }
    return null;
  }

  /**
   * Get player color in a game
   */
  getPlayerColor(game: TurnBasedGame, playerId: string): PlayerColor | null {
    if (game.players.white.id === playerId) return 'white';
    if (game.players.black.id === playerId) return 'black';
    return null;
  }

  /**
   * Check if it's player's turn
   */
  isPlayerTurn(game: TurnBasedGame, playerId: string): boolean {
    const playerColor = this.getPlayerColor(game, playerId);
    return playerColor === game.currentTurn;
  }
}
